import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { type CapiProductPage } from 'types/capi/pdp';

import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import { formatReleaseDate } from '~/utils/dates';
import { formatPrice } from '~/utils/prices';

const InfoItem = ({
  label,
  children
}: {
  children: JSX.Element | string;
  label: string;
}) => {
  return (
    <div className="grid gap-2">
      <div>{label}:</div>
      <div className="font-semibold text-grey-read">{children}</div>
    </div>
  );
};

const ProductInformation = () => {
  const product = usePageData<CapiProductPage>();
  const { t } = useTranslation(['pdp', 'common']);
  const region = useRegion();

  return (
    <div data-cy="product-information" className="grid gap-6 md:gap-10">
      <h2 className="text-lg font-bold">{t('productInfo')}</h2>
      <div className="grid grid-cols-2 gap-8 rounded-lg bg-concrete px-8 py-12 lg:grid-cols-3">
        <InfoItem label={t('productInfoReleaseDate')}>
          {product.date.released
            ? formatReleaseDate(product.date.released, region)
            : t('common:tbcLabel')}
        </InfoItem>
        <InfoItem label={t('productInfoBrand')}>
          <Link className="underline" to={product.brand_path}>
            {product.brand}
          </Link>
        </InfoItem>
        <InfoItem label={t('productInfoModel')}>
          <Link className="underline" to={product.model_path}>
            {product.model}
          </Link>
        </InfoItem>
        <InfoItem label={t('common:productInfoPrice')}>
          {product.price.regular
            ? formatPrice(product.price.regular, product.price.currency)
            : `${t('common:productInfoPrice')} ${t('common:tbcLabel')}`}
        </InfoItem>
        <InfoItem label={t('productInfoStyleCode')}>
          {product.style_code || t('common:tbcLabel')}
        </InfoItem>
        {product.colour && (
          <InfoItem label={t('productInfoColour')}>{product.colour}</InfoItem>
        )}
      </div>
    </div>
  );
};

export default ProductInformation;
